/* eslint-disable no-alert */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makePost, makePatch, signUpload } from "@culturehq/client";
import { Form, ModalDialog, StringField, SubmitButton, NumberField, SelectField, TextField, Button, Icon, classnames } from "@culturehq/components";
import { useDepartments, useLocations, useInterests, useSkills, useValues, useCompanies, useProfile } from "app/context";

import { useDoor, useFieldsLabels, useTryCatch } from "app/hooks";
import { canViewCompaniesField, canViewSkillsField } from "app/permissions";
import { Trans, errorService, getDepartmentFieldCategory, getCompanyFieldCategory, t, isRTX } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import tagsTypes, { getCategoryLabel, getIcon } from "constants/tagsTypes";
import SelectImage from "components/common/SelectImage";
import TrimVideo from "components/stories/TrimVideo";
import VideoOptions from "components/common/VideoOptions";
import ProgressLine from "components/admin/analytics/ProgressLine";
import ChqModalDialog from "components/common/ChqModalDialog";
import StoryPromptOptions from "./StoryPromptOptions";
import ApproveForJobMappingButton from "../stories/ApproveForJobMappingButton";
import KEYS from "../../../stories/post-stories-v2/constants";
import SubPromptModal from "./SubPromptModal";

const VIDEO_TYPE = "video";

const makeSelectOptions = (entities, labelKey, valueKey, categoryIcon, icon, category) => {
  const selectOptions = [];

  Object.keys(entities).forEach(entityId => {
    const { [labelKey]: label, [valueKey]: value } = entities[entityId];
    selectOptions.push({ label, value, categoryIcon, icon, category });
  });

  return selectOptions.sort((a, b) => a.label.localeCompare(b.label));
};

const useSelectOptions = () => {
  const { profile } = useProfile();
  const { companies, onAllCompaniesRequested } = useCompanies();
  const { departments, onAllDepartmentsRequested } = useDepartments();
  const { interests, onAllInterestsRequested } = useInterests();
  const { locations, onAllLocationsRequested } = useLocations();
  const { skills, onAllSkillsRequested } = useSkills();
  const { organizationValues, onAllOrganizationValuesRequested } = useValues();

  useEffect(
    () => {
      if (profile && canViewCompaniesField(profile.organization)) {
        onAllCompaniesRequested();
      }
    }, [profile, onAllCompaniesRequested]
  );

  useEffect(
    () => {
      if (profile && canViewSkillsField(profile.organization)) {
        onAllSkillsRequested();
      }
    }, [profile, onAllSkillsRequested]
  );

  useEffect(() => {
    onAllInterestsRequested();
    onAllLocationsRequested();
    onAllDepartmentsRequested();
    onAllOrganizationValuesRequested();
  }, [onAllDepartmentsRequested, onAllInterestsRequested,
    onAllLocationsRequested, onAllOrganizationValuesRequested]);

  const interestsOptions = makeSelectOptions(interests, "name", "id", "heart-ol", "heart", tagsTypes.INTERESTS);
  const locationOptions = makeSelectOptions(locations, "name", "id", "loc-ol", "ios-location", tagsTypes.LOCATIONS);
  const departmentOptions = makeSelectOptions(departments, "name", "id", "ios-people-outline", "ios-people", getDepartmentFieldCategory(profile.organization.name));
  const orgValuesOptions = makeSelectOptions(organizationValues, "name", "id", "diamond", "diamond-filled", tagsTypes.ORG_VALUES, [], undefined);

  let options = [
    ...orgValuesOptions,
    ...interestsOptions,
    ...locationOptions,
    ...departmentOptions
  ];

  if (profile && canViewCompaniesField(profile.organization)) {
    const companyOptions = makeSelectOptions(companies, "name", "id", "building", "building-filled", getCompanyFieldCategory(profile.organization.name));
    options = [...options, ...companyOptions];
  }

  if (profile && canViewSkillsField(profile.organization)) {
    const skillOptions = makeSelectOptions(skills, "name", "id", "hammer", "hammer-filled", tagsTypes.SKILLS);
    options = [...options, ...skillOptions];
  }

  return {
    options,
    indexes: {
      companies,
      departments,
      interests,
      locations,
      skills,
      organizationValues
    }
  };
};

const Title = ({ storyQuestion, duplicate }) => {
  if (duplicate) return <Trans>Duplicate Story Trend</Trans>;
  return storyQuestion
    ? <Trans>Edit Story Trend</Trans>
    : <Trans>Create New Story Trend</Trans>;
};

const NewStoryQuestionModal = ({
  children, storyQuestion, onCreate, onUpdate, selectedOptions, onReplace, duplicate
}) => {
  const { profile: { organization: { name: orgName } } } = useProfile();
  const [changed, setChanged] = useState(false);
  const onChange = useCallback(() => {
    setChanged(true);
  }, [setChanged]);
  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the trend modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        onClose();
        restartState();
      }
    },
    [changed, onClose, restartState]
  );
  const labels = useFieldsLabels();
  const history = useHistory();
  const { open, onOpen, onClose: onCloseModal } = useDoor(false);
  const { options: selectOptions } = useSelectOptions();
  const [tags, setTags] = useState(selectedOptions?.map(({ entity, entityType }) => (
    {
      category: getCategoryLabel(entityType, labels),
      value: entity?.id,
      label: entity?.name,
      categoryIcon: getIcon(entityType)
    }
  )));
  const [instructionalVideo, setInstructionalVideo] = useState();
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState();
  const [subprompts, setSubprompts] = useState([]);
  const [bannerImage, setBannerImage] = useState();
  const [originalBannerImage, setOriginalBannerImage] = useState();
  const [error, setError] = useState();
  const [trimming, setTrimming] = useState(false);
  const [videoDimension, setVideoDimension] = useState({ height: null, width: null });
  const [progress, setProgress] = useState();
  const [recordedVideo, setRecordedVideo] = useState();
  const [gamificationValue, setGamificationValue] = useState(50);
  const [mappersValueIds, setMappersValueIds] = useState([]);
  const GAMIFICATION_MIN = 0;
  const GAMIFICATION_MAX = 1000;

  const videoDimensionRef = useRef();
  videoDimensionRef.current = videoDimension;

  const restartState = useCallback(
    () => {
      if (!storyQuestion) {
        setGamificationValue(50);
        setInstructionalVideo();
        setBannerImage();
        setOriginalBannerImage();
        setSubprompts([]);
        setTags();
        setMappersValueIds({});
      } else {
        setBannerImage(storyQuestion.imageUrl);
        setInstructionalVideo(storyQuestion.media?.url);
        setVideoThumbnailUrl(storyQuestion.media?.thumbnail);
        if (storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt") {
          setSubprompts([storyQuestion]);
        } else {
          setSubprompts(storyQuestion.childStoryQuestions);
        }
      }
      setChanged(false);
    },
    [storyQuestion]
  );

  useEffect(
    () => {
      if (storyQuestion) {
        setGamificationValue(storyQuestion.points ?? 50);
        setBannerImage(storyQuestion.imageUrl);
        setInstructionalVideo(storyQuestion.media?.url);
        setVideoThumbnailUrl(storyQuestion.media?.thumbnail);
        setMappersValueIds(storyQuestion.externalMapperValueAssociations.map(
          mapperValueAssocaition => (mapperValueAssocaition.externalMapperValue.id)
        ));
        if (storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt") {
          setSubprompts([storyQuestion]);
        } else {
          setSubprompts(storyQuestion.childStoryQuestions);
        }
      }
    }, [storyQuestion]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onClose = () => {
    setProgress();
    onCloseModal();
  };

  const [{ error: tryCatchError }, handleSubmit] = useTryCatch(
    async ({ points, question, summary, visibility, status }) => {
      setError();
      if (!bannerImage) {
        setError(t("Banner image is required"));
        return;
      }
      if (subprompts.length === 0) {
        setError(t("At least one sub prompt is required"));
        return;
      }

      if (status === "hidden" && storyQuestion?.answers > 0 && storyQuestion?.status !== "hidden") {
        const confirmMessage = t(`Any associated stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be removed from the Culture Page. Would you like to proceed?`);
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "active" && storyQuestion?.answers > 0 && storyQuestion?.status === "archived") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be moved out of Archive in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (storyQuestion?.status === "hidden" && status === "active") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be selected for your Culture Page in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "archived" && storyQuestion?.answers > 0 && storyQuestion?.status !== "archived") {
        const confirmMessage = t(`The stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be archived automatically. Do you want to continue?`);
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      let processVideo = false;
      let mediaUpload = 2;
      let localProgress = 0;
      let newImageUrl = null;

      if (instructionalVideo) {
        mediaUpload = 3;
      }

      if (bannerImage && bannerImage.size) {
        newImageUrl = await signUpload(bannerImage, mediaProgress => {
          localProgress = (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
      } else {
        localProgress = (100 / mediaUpload);
        newImageUrl = bannerImage;
      }

      let newOriginalImageUrl = null;
      if (originalBannerImage && originalBannerImage.size) {
        newOriginalImageUrl = await signUpload(originalBannerImage, mediaProgress => {
          localProgress = (100 / mediaUpload) + (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
      } else {
        localProgress += (100 / mediaUpload);
        newOriginalImageUrl = originalBannerImage;
      }

      let newInstructionalVideo = null;
      if (instructionalVideo && instructionalVideo.size) {
        newInstructionalVideo = await signUpload(instructionalVideo, mediaProgress => {
          localProgress = 66 + (mediaProgress / mediaUpload);
          setProgress(localProgress);
        });
        processVideo = true;
      } else {
        if (instructionalVideo) {
          localProgress += (100 / mediaUpload);
        }
        newInstructionalVideo = instructionalVideo;
      }

      function getMediaUrl() {
        if (!instructionalVideo) return undefined;
        if (process.env.NODE_ENV === "development") return KEYS.DEVELOPMENT_VIDEO_URL;

        return newInstructionalVideo;
      }

      let body = {
        imageUrl: newImageUrl,
        mappersValueIds,
        originalImageUrl: newOriginalImageUrl,
        points,
        question,
        tags: tags?.map(tag => {
          if (tag.category) {
            if (isRTX(orgName) && tag.category === tagsTypes.BUSINESSES) {
              return {
                ...tag,
                category: tagsTypes.COMPANIES
              };
            }
            const departmentCategories = [
              tagsTypes.BUSINESSES, tagsTypes.PROFESSIONS, tagsTypes.JOBFUNCTIONS
            ];
            return {
              ...tag,
              category: departmentCategories.includes(tag.category)
                ? tagsTypes.DEPARTMENTS : tag.category
            };
          }

          return tag;
        }),
        summary,
        mediaUrl: getMediaUrl(),
        height: videoDimensionRef.current.height,
        width: videoDimensionRef.current.width,
        web: true,
        processVideo: processVideo || trimming,
        recordedVideo,
        visibility,
        status
      };

      if (storyQuestion) {
        body = {
          ...body,
          subprompts: subprompts.map(subprompt => (
            { ...subprompt, instructions: subprompt.instructions || subprompt.summary }
          )),
          storyQuestionType: storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt" ? "child_prompt" : undefined,
          trimming
        };
      } else {
        body = { ...body, subprompts };
      }

      try {
        await new Promise((resolve, reject) => {
          setTimeout(async () => {
            if (storyQuestion && !duplicate) {
              try {
                restartState();
                const response = await makePatch(`/story_questions/${storyQuestion.id}`, body);
                if (response.storyQuestion) {
                  if (onReplace) {
                    onReplace(response.storyQuestion, storyQuestion.id);
                  } else {
                    onUpdate(response.storyQuestion);
                  }
                }
                onClose();
                resolve(response);
              } catch (error) {
                reject(error);
              }
            } else {
              try {
                const response = await makePost("/story_questions", body);
                if (response.storyQuestion) {
                  restartState();
                  history.push(`/stories/trend/${response.storyQuestion.id}`);
                } else {
                  onClose();
                }
                resolve(response);
              } catch (error) {
                reject(error);
              }
            }
          }, 3000);
        });
        setError();
        onClose();
      } catch (error) {
        setProgress();
        setError(t("Something went wrong, please try again or contact support!"));
        errorService.error(error);
      }
    },
    [duplicate, history, storyQuestion, onClose, onCreate, onReplace, onUpdate, tags, bannerImage,
      instructionalVideo, subprompts, trimming, recordedVideo, mappersValueIds]
  );

  if (tryCatchError && tryCatchError.status !== 422) {
    setError(tryCatchError);
    errorService.error(tryCatchError);
  }

  const onSelectedGroupsChange = value => {
    onChange();
    setTags(value);
  };

  const handleOnMediaChange = async ({
    media,
    thumb,
    isRecordedVideo
  }) => {
    setInstructionalVideo(media);
    onChange();
    if (media.type.startsWith(VIDEO_TYPE)) {
      setRecordedVideo(isRecordedVideo);
      if (thumb) {
        setVideoThumbnailUrl(URL.createObjectURL(thumb));
      }
    }
  };

  const getSelectedMedia = () => {
    if (instructionalVideo?.type?.startsWith(VIDEO_TYPE)) {
      return URL.createObjectURL(instructionalVideo);
    }

    return instructionalVideo;
  };

  const handleVideoTrimmed = (video, editingVideo) => {
    setInstructionalVideo(video);
    onChange();
    setTrimming(editingVideo);
  };

  const handleRemoveVideo = () => {
    setRecordedVideo(undefined);
    setInstructionalVideo(undefined);
    onChange();
  };

  const handleBannerImageSelected = (originalImage, image) => {
    setOriginalBannerImage(originalImage);
    setBannerImage(image);
    onChange();
  };

  const handleNewSubprompt = subprompt => {
    onChange();
    setSubprompts([...subprompts, subprompt]);
  };

  const handleEditSubprompt = subprompt => {
    const newPrompts = subprompts;
    newPrompts[subprompt.index] = subprompt;
    setSubprompts(newPrompts);
    onChange();
  };

  const handleRemoveSubprompt = index => {
    onChange();
    const newPrompts = subprompts;
    newPrompts.splice(index, 1);
    setSubprompts(newPrompts);
  };

  const getButtonLabel = () => (
    storyQuestion ? <Trans>Save Story Trend</Trans> : <Trans>Create Story Trend</Trans>
  );

  const handleSelectedJobMappings = ({ mappersValueIds }) => {
    setMappersValueIds(mappersValueIds);
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose} className="story-question-modal new-story-question-modal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Title storyQuestion={storyQuestion} duplicate={duplicate} />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Form
              onSubmit={handleSubmit}
              initialValues={{
                typesAllowed: "all",
                points: gamificationValue,
                visibility: "private_access",
                status: "active",
                ...storyQuestion
              }}
            >
              {error && <ErrorMessage error={error} />}
              <StringField name="question" autoFocus required placeholder={t("Write your Story Trend title")} onChange={onChange}>
                <Trans>Title</Trans>
              </StringField>
              <div className={classnames("Summary-Container", { "Summary-Container__Video": !!instructionalVideo })}>
                <TextField
                  name="summary"
                  placeholder={t("Share details about what you're looking for and the purpose of this trend so users know why they should contribute")}
                  required
                  onChange={onChange}
                >
                  <Trans>Summary</Trans>
                </TextField>
                <div className="Video-Container">
                  <div className="Video-Container__Wrapper">
                    {instructionalVideo ? (
                      <>
                        <video
                          className="chq-ffd--video"
                          poster={videoThumbnailUrl}
                          src={getSelectedMedia()}
                          disablePictureInPicture
                          controlsList="nodownload"
                          onLoadedMetadata={e => {
                            if (videoDimension.height !== e.target.videoHeight
                              && videoDimension.width !== e.target.videoWidth) {
                              setVideoDimension({
                                height: e.target.videoHeight,
                                width: e.target.videoWidth
                              });
                            }
                          }}
                        />
                        <TrimVideo
                          storyQuestion={storyQuestion}
                          src={getSelectedMedia()}
                          thumbnail={videoThumbnailUrl}
                          onVideoTrimmed={handleVideoTrimmed}
                          fillParent
                        />
                        <Icon className="close-icon" icon="close" onClick={handleRemoveVideo} />
                      </>
                    ) : (
                      <div className="Empty-Video">
                        <Icon icon="video-marketing" />
                        <Trans>Add welcome or instructional video</Trans>
                      </div>
                    )}
                  </div>
                  <VideoOptions handleOnMediaChange={handleOnMediaChange} />
                </div>
              </div>
              <SelectImage
                aspectRatio={14 / 6}
                onImageSelected={handleBannerImageSelected}
                imageToShow={bannerImage}
              />
              <StoryPromptOptions
                onDelete={handleRemoveSubprompt}
                onUpdate={handleEditSubprompt}
                prompts={subprompts}
                onChange={onChange}
              />
              <SubPromptModal
                parentStoryQuestion={storyQuestion}
                onUpdate={() => {
                  onChange();
                  onUpdate();
                }}
                onCreate={handleNewSubprompt}
              >
                {onTrigger => (
                  <Button className={classnames("Subprompt-Add_Btn", { EmptySubPrompts: subprompts.length === 0 })} inverted onClick={onTrigger}>
                    <Icon icon="ios-plus" />
                    <Trans>
                      {
                        subprompts.length === 0
                          ? "Add Your First Prompt"
                          : "Add Another Prompt"
                      }
                    </Trans>
                  </Button>
                )}
              </SubPromptModal>
              <NumberField
                name="points"
                placeholder={t("Choose how much users should earn for contributing")}
                required
                onChange={value => {
                  const numericValue = parseInt(value, 10);
                  if (!Number.isNaN(numericValue)) {
                    if ((numericValue >= GAMIFICATION_MIN)
                      && (numericValue <= GAMIFICATION_MAX)) {
                      setGamificationValue(numericValue);
                      onChange();
                    }
                  }
                }}
                min={GAMIFICATION_MIN}
                max={GAMIFICATION_MAX}
                value={gamificationValue.toString()}
              >
                <Trans>Gamification</Trans>
              </NumberField>
              <SelectField
                className="share-with-field"
                creatable
                name="allOptions"
                options={selectOptions}
                placeholder={t(`Select default ${labels.departments.plural.toLowerCase()}, locations, interests, etc.`)}
                multiple
                onChange={onSelectedGroupsChange}
                value={tags}
              >
                <Trans>Default Tags</Trans>
              </SelectField>
              <SelectField
                name="visibility"
                required
                options={[
                  { value: "private_access", label: t("Private") },
                  { value: "public_access", label: t("Public") }
                ]}
                onChange={onChange}
              >
                <Trans>Visibility</Trans>
              </SelectField>
              <SelectField
                name="status"
                required
                options={[
                  { value: "active", label: t("Active") },
                  { value: "paused", label: t("Paused") },
                  { value: "hidden", label: t("Hidden") },
                  { value: "archived", label: t("Archived") }
                ]}
                onChange={onChange}
              >
                <Trans>Status</Trans>
              </SelectField>
              {progress && (
                <div className="progressline-trend">
                  <ProgressLine
                    className="story-progressbar"
                    color="#79b17d"
                    valueNow={progress}
                    valueMax={100}
                  />
                </div>
              )}
              <div className="Footer-Buttons">
                <SubmitButton primary>
                  {submitting => (
                    submitting ? <Trans>Submitting...</Trans> : getButtonLabel()
                  )}
                </SubmitButton>
                <ApproveForJobMappingButton
                  mappersValueIds={mappersValueIds}
                  onUpdate={value => {
                    handleSelectedJobMappings(value);
                  }}
                  storyQuestion={storyQuestion}
                />
              </div>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default NewStoryQuestionModal;
